<script>
import {
  getRubrics,
  getSystemRubrics,
  getRecentRubrics,
  deleteSingleRubric,
} from "@/api";
import EmptyState from "@/components/global/empty_state.vue";
import RubricFilter from "./rubric_filter.vue";
import RubricLibraryViewList from "./rubric_library_view_list.vue";
import RubricShareModal from "@/components/rubrics/rubric_share_modal.vue";

export default {
  name: "RubricLibraryView",

  components: {
    EmptyState,
    RubricFilter,
    RubricLibraryViewList,
  },

  props: {
    value: {
      type: Object,
      default: () => {},
    },

    showAddNew: {
      type: Boolean,
      default: false,
    },

    showShare: {
      type: Boolean,
      default: false,
    },

    showEdit: {
      type: Boolean,
      default: false,
    },

    showDelete: {
      type: Boolean,
      default: false,
    },

    isRubricCard: {
      type: Boolean,
      default: true,
    },
    filtersSize: {
      type: String,
      default: "small",
    },
  },

  emits: ["selectRubric"],

  data() {
    return {
      platformFilters: {
        name: "",
        gradeLevel: null,
        intensity: null,
        languageId: null,
      },
      myFilters: {
        name: "",
        gradeLevel: null,
        intensity: null,
        languageId: null,
      },
      sharedFilters: {
        name: "",
        gradeLevel: null,
        intensity: null,
        languageId: null,
      },
      rubricsLoading: false,
      rubrics: [],
      sharedRubrics: [],
      platformRubrics: [],
      recentRubrics: [],
      activeTab: 0,
    };
  },

  computed: {
    rubricActionsListeners() {
      const listeners = {};

      if (this.$listeners["selectRubric"]) {
        listeners["select-rubric"] = (value) =>
          this.$emit("selectRubric", value);
      }

      if (this.showEdit) {
        listeners["edit-rubric"] = (value) => {
          this.$router.push({
            name: "rubric-edit",
            params: { id: value.id },
          });
        };
      }

      if (this.showShare) {
        listeners["share-rubric"] = (value) => {
          this.$egModal.show("rubric-share-modal", {
            component: RubricShareModal,
            props: {
              rubric: value,
              zIndex: 1060,
              showBackdrop: true,
            },
          });
        };
      }

      if (this.showDelete) {
        listeners["delete-rubric"] = async (value) => {
          const isConfirmed = await this.$showDeleteConfirmationModal({
            title: this.$t("rubrics.deleteRubricConfirm"),
            message: this.$t("rubrics.rubricCannotBeAccessed"),
          });

          if (!isConfirmed) return;

          this.deleteRubric(value.id);
        };
      }

      return listeners;
    },

    groupedRubrics() {
      return this.rubrics.reduce((acc, rubric) => {
        const type = rubric.type;

        if (!acc[type]) {
          acc[type] = [];
        }

        acc[type].push(rubric);

        return acc;
      }, {});
    },

    filteredRecentRubrics() {
      return this.filterRubrics(this.recentRubrics, this.myFilters);
    },

    filteredRubricLibrary() {
      return this.filterRubrics(this.platformRubrics, this.platformFilters);
    },

    filteredSelfRubrics() {
      return this.filterRubrics(this.rubrics, this.myFilters);
    },

    filteredSelfRubricsWithoutRecent() {
      const recentIds = new Set(this.recentRubrics.map((r) => r.id));
      return this.filteredSelfRubrics.filter(
        (rubric) => !recentIds.has(rubric.id)
      );
    },

    filteredSharedRubrics() {
      return this.filterRubrics(this.sharedRubrics, this.sharedFilters);
    },

    isPlatformFiltered() {
      return this.hasActiveFilters(this.platformFilters);
    },

    isMyFiltered() {
      return this.hasActiveFilters(this.myFilters);
    },

    isSharedFiltered() {
      return this.hasActiveFilters(this.sharedFilters);
    },
  },

  async mounted() {
    this.init();

    if (this.$route.query.rubricId) {
      // this.$refs.sidebarRef.localShow = true;
      const rubric = this.rubrics.find(
        (rubric) => +rubric.id === +this.$route.query.rubricId
      );

      rubric && this.$emit("selectRubric", rubric);
    }

    // Listen for rubric creation events to switch to "My rubrics" tab
    this.$eventBus.$on("rubric-created", this.rubricCreatedHandler);
  },

  beforeDestroy() {
    this.$eventBus.$off("rubric-created", this.rubricCreatedHandler);
  },

  methods: {
    hasActiveFilters(filters) {
      return Object.values(filters).some((value) => Boolean(value));
    },
    async rubricCreatedHandler(eventData) {
      // Handle both old format (direct rubric) and new format ({ rubric, isCreating })
      const rubric = eventData?.rubric;
      const isCreating =
        eventData?.isCreating !== undefined ? eventData.isCreating : true;
      this.activeTab = 1;
      // Only refresh rubrics if this is a creation event
      if (isCreating) {
        await this.refreshMyRubrics();

        if (rubric) {
          this.$emit("selectRubric", rubric);
        }
      }
    },

    filterRubrics(rubrics, filters) {
      return rubrics.filter((rubric) => {
        // Search query filter
        if (
          filters.name &&
          !rubric.name.toLowerCase().includes(filters.name.toLowerCase())
        ) {
          return false;
        }

        // Grade level filter
        if (filters.gradeLevel && rubric.gradeLevel !== filters.gradeLevel) {
          return false;
        }

        // Intensity filter
        if (filters.intensity && rubric.intensity !== filters.intensity) {
          return false;
        }

        // Language filter
        if (filters.languageId && rubric.language?.id !== filters.languageId) {
          return false;
        }

        return true;
      });
    },

    async init() {
      this.rubricsLoading = true;
      const [my, shared, system, recent] = await Promise.all([
        this.fetchRubrics(true),
        this.fetchRubrics(false),
        this.fetchSystemRubrics(),
        this.fetchRecentRubrics(),
      ]);
      this.rubrics = my;
      this.sharedRubrics = shared;
      this.platformRubrics = system;
      this.recentRubrics = recent;

      this.activeTab = this.rubrics?.length === 0 ? 0 : 1;

      this.rubricsLoading = false;
    },

    async refreshMyRubrics() {
      // Optimized refresh for my rubrics only after creation
      this.rubrics = await this.fetchRubrics(true);
    },

    async fetchRubrics(my) {
      try {
        const { data } = await getRubrics(my);

        return data ?? [];
      } catch (error) {
        this.$showToastError(error.response?.data?.error);
        return [];
      }
    },
    async fetchSystemRubrics() {
      try {
        const { data } = await getSystemRubrics();
        return data ?? [];
      } catch (error) {
        this.$showToastError(error.response?.data?.error);
        return [];
      }
    },

    async fetchRecentRubrics() {
      try {
        const { data } = await getRecentRubrics(10);
        return data ?? [];
      } catch (error) {
        // Silently fail for recent rubrics, as it's an optional feature
        console.error("Failed to fetch recent rubrics:", error);
        return [];
      }
    },

    async deleteRubric(id) {
      try {
        await deleteSingleRubric(id);
        this.init();
      } catch (error) {
        this.$showToastError(error.response?.data?.error);
      }
    },
  },
};
</script>

<template>
  <eg-b-skeleton-wrapper :loading="rubricsLoading">
    <template #loading>
      <eg-b-skeleton-table />
    </template>
    <eg-tabs v-model="activeTab" data-testid="rubrics-tabs">
      <eg-b-tab
        title-item-class="rubrics-tabs-platform"
        :title="$t('rubrics.platformRubrics')"
        :active="activeTab === 0"
      >
        <rubric-filter v-model="platformFilters" :filters-size="filtersSize" />

        <rubric-library-view-list
          v-if="filteredRubricLibrary.length > 0"
          :rubrics="filteredRubricLibrary"
          :value="value"
          :is-rubric-card="isRubricCard"
          is-platform-rubric
          v-on="rubricActionsListeners"
        />

        <empty-state
          v-else-if="isPlatformFiltered"
          :title="$t('rubrics.noRubricsFound')"
          :description="$t('rubrics.noRubricsFoundDescription')"
          class="mt-4 ml-auto mr-auto"
        />

        <empty-state
          v-else
          :title="$t('rubrics.noRubricsInLibrary')"
          :description="$t('rubrics.noRubricsInLibraryDescription')"
          class="mt-4 ml-auto mr-auto"
        />
      </eg-b-tab>

      <eg-b-tab
        title-item-class="rubrics-tabs-my-rubric"
        :title="$t('rubrics.myRubrics')"
        :active="activeTab === 1"
      >
        <rubric-filter v-model="myFilters" :filters-size="filtersSize" />

        <div
          v-if="filteredRecentRubrics.length > 0"
          class="recently-used-section"
        >
          <div class="section-title">{{ $t("rubrics.recentlyUsed") }}</div>
          <rubric-library-view-list
            :rubrics="filteredRecentRubrics"
            :value="value"
            :is-rubric-card="isRubricCard"
            v-on="rubricActionsListeners"
          />
        </div>

        <rubric-library-view-list
          v-if="filteredSelfRubricsWithoutRecent.length > 0"
          :rubrics="filteredSelfRubricsWithoutRecent"
          :value="value"
          :is-rubric-card="isRubricCard"
          v-on="rubricActionsListeners"
        />

        <empty-state
          v-if="
            isMyFiltered &&
            filteredRecentRubrics.length === 0 &&
            filteredSelfRubricsWithoutRecent.length === 0
          "
          :title="$t('rubrics.noRubricsFound')"
          :description="$t('rubrics.noRubricsFoundDescription')"
          class="mt-4 ml-auto mr-auto"
        />

        <empty-state
          v-else-if="
            !rubricsLoading &&
            !isMyFiltered &&
            filteredRecentRubrics.length === 0 &&
            filteredSelfRubricsWithoutRecent.length === 0
          "
          key="empty"
          :title="
            showAddNew
              ? $t('rubrics.noRubricsInLibrary')
              : $t('rubrics.noRubricsYet')
          "
          :description="
            showAddNew ? $t('rubrics.noRubricsInLibraryDescription') : ''
          "
          class="m-auto"
          data-testid="empty-state"
        />
      </eg-b-tab>

      <eg-b-tab
        v-if="sharedRubrics.length > 0"
        :title="$t('rubrics.sharedRubrics')"
      >
        <rubric-filter v-model="sharedFilters" :filters-size="filtersSize" />

        <rubric-library-view-list
          v-if="filteredSharedRubrics.length > 0"
          :rubrics="filteredSharedRubrics"
          :value="value"
          :is-rubric-card="isRubricCard"
          v-on="rubricActionsListeners"
        />

        <empty-state
          v-else-if="isSharedFiltered"
          :title="$t('rubrics.noRubricsFound')"
          :description="$t('rubrics.noRubricsFoundDescription')"
          class="mt-4 ml-auto mr-auto"
        />

        <empty-state
          v-else
          :title="$t('rubrics.noRubricsInLibrary')"
          description=""
          class="mt-4 ml-auto mr-auto"
        />
      </eg-b-tab>
    </eg-tabs>
  </eg-b-skeleton-wrapper>
</template>

<style lang="scss" scoped>
.title-text {
  color: var(--Text-Body, #212529);
  font-size: 24px;
  font-weight: 500;
  line-height: 28px;
}

.recently-used-section {
  margin-bottom: 16px;
  padding: 8px;
  border-radius: 8px;
  border: 1px solid var(--colors-purple-200);
}

.section-title {
  color: var(--Text-Body, #212529);
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  margin-bottom: 12px;
  padding-left: 4px;
}

::v-deep .rubric-container:last-of-type {
  margin-bottom: 1rem;
}
</style>
<style lang="scss">
.eg-card {
  padding: 0;
}

.rubrics-view .tab-content {
  height: calc(100vh - 295px - var(--maintenance-height));
  overflow: hidden;
}

.rubrics-view .tab-pane {
  padding: 16px 24px;
  background-color: var(--color-surface-neutral-canvas);

  &.active {
    height: calc(100vh - 295px - var(--maintenance-height));
    overflow-y: auto;
    padding-top: 0;
    .empty-state {
      margin-top: 50% !important;
    }
  }
}

.rubric-library-cards {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.rubric-actions {
  display: flex;
  gap: 12px;
}
</style>
